"use client";
import { useState, useEffect, useRef } from "react";
import {
  Play,
  X,
  GraduationCap,
  FlaskConical,
  MessageCircleHeart,
  ExternalLink,
} from "lucide-react";
import { Button } from "@ui/button";
import Image from "next/image";

interface Video {
  youtubeId: string;
  title: string;
  category: "class" | "practical" | "testimony";
  thumbnail?: string;
}

const videos: Video[] = [
  {
    youtubeId: "p-Wpqk89wcE",
    title: "Pathfinder Class - TCM 112",
    category: "class",
  },
  {
    youtubeId: "hqyiRmOpzWM",
    title: "Anatomy class by Associate Prof. Olaniyan",
    category: "class",
  },
  {
    youtubeId: "IqxaGbG3Z4g",
    title: "Hands-on Acupuncture Training Session at OICNM",
    category: "practical",
  },
  {
    youtubeId: "OG83Ucld_1o",
    title: "Hands-on Biochemistry Practical",
    category: "practical",
  },
  {
    youtubeId: "Ws6OabShR0I",
    title: "Inquiry: one of the 4 pillars of TCM diagnosis",
    category: "class",
  },
  {
    youtubeId: "H4cAvBvoe4E",
    title:
      "Associate Prof. Olaniyan Olugbemi Teaching OICNM students Anatomy and Physiology",
    category: "class",
  },
  {
    youtubeId: "_0Tt0qDZjQc",
    title: "Students during TCM consultation for a patient",
    category: "practical",
  },
  {
    youtubeId: "BsaOCPofcG0",
    title:
      "Non Invasive Brain Stimulation Procedure For Pain and Paralysis Management",
    category: "practical",
  },
  {
    youtubeId: "P4hrsg-J5Pc",
    title: "Naturopathy student speaks about his experience studying at OICNM",
    category: "testimony",
  },
  {
    youtubeId: "Uh7ECUfqNes",
    title:
      "Doctor of Naturopathic Medicine Student Talks About Her Experience Studying At OICNM",
    category: "testimony",
  },
  {
    youtubeId: "1VGyp2A72yw",
    title: "REVIEW: Best Natural Medicine College In Nigeria",
    category: "testimony",
  },
  {
    youtubeId: "3Qz2cVLAwpU",
    title: "Intriguing review from a Naturopathic Medicine student at OICNM",
    category: "testimony",
  },
];
//3Qz2cVLAwpU
const categoryConfig = {
  class: {
    label: "Class Sessions",
    icon: GraduationCap,
    color: "bg-healing-600",
  },
  practical: {
    label: "Practical Sessions",
    icon: FlaskConical,
    color: "bg-wisdom-600",
  },
  testimony: {
    label: "Student Testimonies",
    icon: MessageCircleHeart,
    color: "bg-nature-600",
  },
};

type VideoGalleryProps = {
  limit?: number; // Number of videos to show (for homepage preview)
};
export const VideoGallerySection = ({ limit }: VideoGalleryProps) => {
  const [activeFilter, setActiveFilter] = useState<"all" | Video["category"]>(
    "all",
  );
  const [playingVideo, setPlayingVideo] = useState<Video | null>(null);
  // Render the original order on the server and the first client render so the
  // markup matches during hydration; shuffle only after mount. Shuffling during
  // render would run Math.random() twice (server + client) and produce a
  // hydration mismatch. [SC 4.1.1]
  const [shuffledVideos, setShuffledVideos] = useState<Video[]>(videos);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setShuffledVideos((prev) => [...prev].sort(() => Math.random() - 0.5));
  }, []);

  // Close the lightbox on Escape; restore focus to the opener. [SC 2.1.2]
  useEffect(() => {
    if (!playingVideo) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPlayingVideo(null);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      triggerRef.current?.focus?.();
    };
  }, [playingVideo]);

  const filteredVideos =
    activeFilter === "all"
      ? limit !== undefined
        ? shuffledVideos.slice(0, limit)
        : shuffledVideos
      : shuffledVideos.filter((v) => v.category === activeFilter);

  const filters: { key: "all" | Video["category"]; label: string }[] = [
    { key: "all", label: "All Videos" },
    { key: "class", label: "Class Sessions" },
    { key: "practical", label: "Practicals" },
    { key: "testimony", label: "Testimonies" },
  ];

  const getThumbnail = (youtubeId: string) =>
    `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

  return (
    <>
      <section
        id="videos"
        className="py-20 md:py-28 bg-gradient-to-b from-healing-50/40 to-background"
      >
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-healing-600 font-sans-body text-sm font-semibold tracking-widest uppercase mb-3">
              Inside OICNM
            </span>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Experience Our{" "}
              <span className="text-healing-600">Learning Environment</span>
            </h2>
            <p className="text-muted-foreground text-base md:text-lg">
              Watch real class sessions, hands-on practicals, and hear directly
              from our students about their transformative journey.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12">
            {filters.map((f) => (
              <Button
                key={f.key}
                variant={activeFilter === f.key ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveFilter(f.key)}
                className={
                  activeFilter === f.key
                    ? "bg-healing-700 hover:bg-healing-800 text-white border-healing-700"
                    : "border-healing-200 text-healing-700 hover:bg-healing-800 hover:border-healing-50"
                }
              >
                {f.label}
              </Button>
            ))}
          </div>

          {/* Video Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredVideos.map((video) => {
              const cat = categoryConfig[video.category];
              const CatIcon = cat.icon;
              return (
                <button
                  key={video.youtubeId}
                  type="button"
                  aria-label={`Play video: ${video.title}`}
                  className="group relative rounded-2xl overflow-hidden bg-card shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1 cursor-pointer text-left w-full border-0 p-0"
                  onClick={(e) => {
                    triggerRef.current = e.currentTarget;
                    setPlayingVideo(video);
                  }}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video overflow-hidden">
                    <Image
                      src={video.thumbnail || getThumbnail(video.youtubeId)}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      width={160}
                      height={160}
                    />
                    {/* Dark overlay */}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors duration-300" />

                    {/* Play button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-6 h-6 md:w-7 md:h-7 text-healing-700 fill-healing-700 ml-1" aria-hidden="true" />
                      </div>
                    </div>

                    {/* Category badge */}
                    <div
                      className={`absolute top-3 left-3 ${cat.color} text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5`}
                    >
                      <CatIcon className="w-3 h-3" aria-hidden="true" />
                      {cat.label}
                    </div>
                  </div>

                  {/* Title */}
                  <div className="p-4 md:p-5">
                    <h3 className="font-serif-display font-semibold text-foreground text-sm md:text-base leading-snug line-clamp-2">
                      {video.title}
                    </h3>
                  </div>
                </button>
              );
            })}
          </div>

          {/* YouTube Channel CTA */}
          <div className="mt-14 text-center box-border">
            <div className="box-border overflow-hidden inline-flex flex-col items-center gap-4 p-4 md:p-8 rounded-2xl bg-healing-50/60 border border-healing-100">
              <p className="font-serif-display text-xl md:text-2xl font-semibold text-foreground">
                Want to see more videos?
              </p>
              <p className="text-muted-foreground text-sm md:text-base max-w-md">
                Visit our YouTube channel for the full collection of lectures,
                practicals, and student stories.
              </p>
              <a
                href="https://www.youtube.com/@theoicnm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  className="bg-healing-600 hover:bg-healing-700 text-white font-sans-body font-semibold px-8 py-5 h-auto gap-2"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-5 h-5 fill-current"
                    aria-hidden="true"
                  >
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                  Visit Our YouTube Channel
                  <ExternalLink className="w-4 h-4" aria-hidden="true" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {playingVideo && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Video: ${playingVideo.title}`}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setPlayingVideo(null)}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <Button
              ref={closeBtnRef}
              type="button"
              aria-label="Close video"
              variant="ghost"
              size="icon"
              className="absolute -top-12 right-0 text-white hover:bg-white/20"
              onClick={() => setPlayingVideo(null)}
            >
              <X className="w-6 h-6" aria-hidden="true" />
            </Button>

            {/* Embedded YouTube player */}
            <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-2xl">
              <iframe
                src={`https://www.youtube.com/embed/${playingVideo.youtubeId}?autoplay=1&rel=0`}
                title={playingVideo.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            {/* Title below video */}
            <p className="text-white/80 text-sm md:text-base mt-4 font-sans-body text-center">
              {playingVideo.title}
            </p>
          </div>
        </div>
      )}
    </>
  );
};
